import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from './styles/TableOfContents.module.css';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useCanvas } from "../../../context/CanvasContext";
import { useEditorUI } from "../../../context/EditorUIContext";
import TableOfContentsModal from "./TableOfContentsModal";
import useIsMobile from "../../../utils/useIsMobile";
import { FaArrowLeft, FaPlus, FaTrashAlt, FaSave, FaTimes, FaGripVertical, FaMinus, FaBars, FaEyeSlash } from 'react-icons/fa';
import cloneDeep from 'lodash/cloneDeep';

/**
 * The table of contents editor.
 *
 * The table of contents is DRAFT state now, like the graph it points into: every
 * gesture here dispatches one `setTableOfContents` op, which the op queue
 * debounces and the server rebroadcasts, and none of it reaches a player until the
 * scenario is published. It used to be a PATCH per gesture straight onto the live
 * scenario, so reordering a section changed what players saw mid-read.
 *
 * The op is whole-document rather than per-gesture because the table of contents is
 * creator-only — one writer, so a finer vocabulary would buy nothing.
 */

const emptyToc = () => ({ contents: [{ sections: [], items: [] }], disabled: false });

/**
 * Fills in the shape the editor below assumes, without mutating its input — the
 * draft it is handed is React state shared with the rest of the editor.
 */
const normalizeToc = (toc) => {
    if (!toc?.contents?.length || !Array.isArray(toc.contents[0]?.sections)) return emptyToc();

    return {
        ...toc,
        disabled: !!toc.disabled,
        contents: toc.contents.map((content, index) =>
            index === 0
                ? {
                      ...content,
                      items: Array.isArray(content.items) ? content.items : [],
                      sections: (content.sections || []).map((section) => ({
                          ...section,
                          items: Array.isArray(section.items) ? section.items : [],
                          behavior: section.behavior || 'alwaysAccessible',
                      })),
                  }
                : content
        ),
    };
};

const TableOfContents = () => {
    const { nodes, tableOfContents: draftToc, setTableOfContents: commitToc } = useCanvas();
    const { setShowTocEditor } = useEditorUI();
    const navigate = useNavigate();

    // Table-of-contents entries point at situation ids, and a situation id IS a
    // node id. Shaped like the Situation documents this component and its modal
    // have always taken, so nothing below had to change.
    const situations = useMemo(
        () => nodes.map((node) => ({ _id: node.id, title: node.data?.title || '' })),
        [nodes]
    );

    const [tableOfContents, setTableOfContents] = useState(() => normalizeToc(draftToc));

    const [isSectionInputVisible, setIsSectionInputVisible] = useState(false);
    const [isEditingSection, setIsEditingSection] = useState(false);
    const [sectionLabel, setSectionLabel] = useState("");
    const [selectedSituation, setSelectedSituation] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [itemLabel, setItemLabel] = useState('');
    const [currentSectionIndex, setCurrentSectionIndex] = useState(null);
    const [currentItemIndex, setCurrentItemIndex] = useState(null);
    const [editingSectionOriginalLabel, setEditingSectionOriginalLabel] = useState("");

    const [sectionBehavior, setSectionBehavior] = useState('alwaysAccessible');
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
    const [addAllSituations, setAddAllSituations] = useState(false);

    const inputRef = useRef(null);

    // Follows the draft, which is also how a collaborator's change arrives: the
    // server rebroadcasts their op, the draft updates, and this re-seeds from it.
    useEffect(() => {
        setTableOfContents(normalizeToc(draftToc));
    }, [draftToc]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (inputRef.current && !inputRef.current.contains(event.target) && isEditingSection) {
                 const editControls = inputRef.current.closest(`.${styles.sectionEdit}`);
                 if (!editControls || !editControls.contains(event.target)) {
                    cancelEditing();
                 }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isEditingSection]);

    
    /**
     * Commits an edit. Local state moves first so the list never lags a keystroke,
     * then the op goes onto the queue — which debounces a burst of edits into one
     * write and reports failure through the editor's save-status pill.
     */
    const saveTableOfContents = (newTableOfContents) => {
        const next = normalizeToc(newTableOfContents);
        setTableOfContents(next);
        commitToc(next);
    };

    const disableToC = async (disableValue) => {
        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));

        if (!updatedTableOfContents) {
            console.error("nothing", sectionIndex);
            return;
        }

        updatedTableOfContents.disabled = disableValue;

        await saveTableOfContents(updatedTableOfContents);
    }

    

    const createSection = () => {
        setIsSectionInputVisible(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleAddSection = async () => {
        if (!sectionLabel.trim()) {
            return;
        }

        const currentToc = tableOfContents || emptyToc();
        if (!currentToc.contents || currentToc.contents.length === 0) {
            currentToc.contents = [ { sections: [], items: [] } ];
        }
        if (!currentToc.contents[0].sections) {
             currentToc.contents[0].sections = [];
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(currentToc));
        const newSection = { 
            label: sectionLabel.trim(), 
            items: [], 
            disabled: false, 
            behavior: sectionBehavior 
        };
        updatedTableOfContents.contents[0].sections.push(newSection);

        await saveTableOfContents(updatedTableOfContents);

        setIsSectionInputVisible(false);
        setSectionLabel('');
        setSectionBehavior('alwaysAccessible');
        setAddAllSituations(false);
        setShowAdvancedSettings(false);
    };

    const handleKeyPressSection = (e) => {
        if (e.key === "Enter") {
            handleAddSection();
        }
    };

    const startEditing = (sectionIndex, label) => {
        setCurrentSectionIndex(sectionIndex);
        setIsEditingSection(true);
        setSectionLabel(label);
        setEditingSectionOriginalLabel(label);
        setIsSectionInputVisible(false);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const handleSectionEditConfirm = async (sectionIndex) => {
        if (!sectionLabel.trim()) {
             setSectionLabel(editingSectionOriginalLabel);
             setIsEditingSection(false);
             setCurrentSectionIndex(null);
             return;
        }
        if (sectionLabel.trim() === editingSectionOriginalLabel) {
            cancelEditing();
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        if (updatedTableOfContents?.contents?.[0]?.sections?.[sectionIndex]) {
            updatedTableOfContents.contents[0].sections[sectionIndex].label = sectionLabel.trim();
            await saveTableOfContents(updatedTableOfContents);
        } else {
            console.error("Error updating section: Invalid structure or index.");
        }

        cancelEditing();
    };

    const handleSectionEditKeyPress = (e, sectionIndex) => {
         if (e.key === "Enter") {
             handleSectionEditConfirm(sectionIndex);
         } else if (e.key === "Escape") {
             cancelEditing();
         }
     };

    const cancelEditing = () => {
        setIsEditingSection(false);
        setCurrentSectionIndex(null);
        setSectionLabel("");
        setEditingSectionOriginalLabel("");
    };


    const handleDeleteSection = async (sectionIndexToDelete) => {
        if (!window.confirm(`Are you sure you want to delete the section "${tableOfContents.contents[0].sections[sectionIndexToDelete].label}" and all its items?`)) {
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        if (updatedTableOfContents?.contents?.[0]?.sections) {
            updatedTableOfContents.contents[0].sections.splice(sectionIndexToDelete, 1);
            await saveTableOfContents(updatedTableOfContents);
        } else {
             console.error("Error deleting section: Invalid structure or index.");
        }
    };

    

    const toggleModal = (event, sectionIndex, itemIndex = null, item = null) => {
        if (event) {
            event.stopPropagation();
        }

        setIsModalVisible(!isModalVisible);

        if (!isModalVisible) {
            setCurrentSectionIndex(sectionIndex);
            setCurrentItemIndex(itemIndex);
            setItemLabel(item ? item.label : '');
            const currentSituation = item && item.situation ? situations.find(sit => sit._id === item.situation) : null;
            setSelectedSituation(currentSituation || null);
        } else {
             setCurrentSectionIndex(null);
             setCurrentItemIndex(null);
             setItemLabel('');
             setSelectedSituation(null);
        }
    };

    const handleSaveItem = async () => {
        if (!itemLabel.trim() || !selectedSituation?._id) {
            alert("Please provide a label and select a situation.");
            return;
        }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const targetSection = updatedTableOfContents?.contents?.[0]?.sections?.[currentSectionIndex];

        if (!targetSection) {
             console.error("Error saving item: Target section not found.");
             setIsModalVisible(false);
             setCurrentSectionIndex(null);
             setCurrentItemIndex(null);
             setItemLabel('');
             setSelectedSituation(null);
             return;
        }
         if (!Array.isArray(targetSection.items)) {
            targetSection.items = [];
        }


        const newItemData = { label: itemLabel.trim(), situation: selectedSituation._id };

        if (currentItemIndex === null) {
            targetSection.items.push(newItemData);
        } else {
            if (currentItemIndex >= 0 && currentItemIndex < targetSection.items.length) {
                 targetSection.items[currentItemIndex] = newItemData;
            } else {
                 console.error("Error updating item: Invalid item index.");
                 setIsModalVisible(false);
                 setCurrentSectionIndex(null);
                 setCurrentItemIndex(null);
                 setItemLabel('');
                 setSelectedSituation(null);
                 return;
            }
        }

        await saveTableOfContents(updatedTableOfContents);

        setIsModalVisible(false);
        setItemLabel('');
        setSelectedSituation(null);
        setCurrentSectionIndex(null);
        setCurrentItemIndex(null);
    };

    const handleAddAllSituationsToNewSection = async () => {
        if (!Array.isArray(situations) || situations.length === 0) {
            alert("No situations to add.");
            return;
        }

        const updatedTableOfContents = cloneDeep(tableOfContents);
        if (!updatedTableOfContents.contents || !Array.isArray(updatedTableOfContents.contents)) {
            updatedTableOfContents.contents = [{ sections: [] }];
        }

        const newSection = {
            label: sectionLabel,
            items: situations.map((situation) => ({
                label: situation.title || "Untitled",
                situation: situation._id,
            })),
            disabled: false,
            behavior: sectionBehavior,
        };

        updatedTableOfContents.contents[0].sections.push(newSection);

        await saveTableOfContents(updatedTableOfContents);

        setIsSectionInputVisible(false);
        setSectionLabel('');
        setSectionBehavior('alwaysAccessible');
        setAddAllSituations(false);
        setShowAdvancedSettings(false);
    };


    const handleDeleteItem = async (sectionIndex, itemIndexToDelete) => {

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const targetSection = updatedTableOfContents?.contents?.[0]?.sections?.[sectionIndex];

        if (targetSection?.items && itemIndexToDelete >= 0 && itemIndexToDelete < targetSection.items.length) {
            targetSection.items.splice(itemIndexToDelete, 1);
            await saveTableOfContents(updatedTableOfContents);
        } else {
            console.error("Error deleting item: Invalid structure or index.");
        }
    };

    

    const onDragEnd = (result) => {
        const { source, destination, type } = result;

        if (!destination || !tableOfContents?.contents?.[0]) {
            return;
        }

        const sourceContentIndex = 0;
        const destinationContentIndex = 0; 

         if (!tableOfContents.contents[sourceContentIndex]?.sections) {
             console.error("DragEnd Error: Source sections array is missing.");
             return;
         }

        const updatedTableOfContents = JSON.parse(JSON.stringify(tableOfContents));
        const sections = updatedTableOfContents.contents[sourceContentIndex].sections;


         if (type === 'section') {
             if (source.droppableId === destination.droppableId && source.index === destination.index) {
                 return;
             }
            if (source.droppableId === 'toc-sections' && destination.droppableId === 'toc-sections') {
                 const [reorderedSection] = sections.splice(source.index, 1);
                 sections.splice(destination.index, 0, reorderedSection);

                 saveTableOfContents(updatedTableOfContents);
            } else {
                 console.warn("Unsupported section drag operation between different droppables.");
            }
         }
        else if (type === 'item') {
            const sourceDroppableIdParts = source.droppableId.split('-');
            const destinationDroppableIdParts = destination.droppableId.split('-');

            if (sourceDroppableIdParts.length < 2 || destinationDroppableIdParts.length < 2) {
                 console.error("DragEnd Error: Invalid droppable ID format for items.");
                 return;
            }

            const sourceSectionIndex = parseInt(sourceDroppableIdParts[1], 10);
            const destinationSectionIndex = parseInt(destinationDroppableIdParts[1], 10);

             if (isNaN(sourceSectionIndex) || isNaN(destinationSectionIndex) ||
                 sourceSectionIndex < 0 || sourceSectionIndex >= sections.length ||
                 destinationSectionIndex < 0 || destinationSectionIndex >= sections.length) {
                  console.error("DragEnd Error: Invalid section index parsed from droppable ID.");
                  return;
             }
              if (!sections[sourceSectionIndex]?.items) {
                   console.error("DragEnd Error: Source section items array is missing.");
                   return;
              }
              if (!sections[destinationSectionIndex]?.items) {
                  console.error("DragEnd Error: Destination section items array is missing.");
                   if(sections[destinationSectionIndex]) {
                       sections[destinationSectionIndex].items = [];
                   } else {
                       return;
                   }
              }

            if (source.droppableId === destination.droppableId) {
                if (source.index === destination.index) {
                    return;
                }
                const sectionItems = sections[sourceSectionIndex].items;
                const [movedItem] = sectionItems.splice(source.index, 1);
                sectionItems.splice(destination.index, 0, movedItem);
            }
            else {
                const sourceSectionItems = sections[sourceSectionIndex].items;
                 const destinationSectionItems = sections[destinationSectionIndex].items;

                 if (!Array.isArray(destinationSectionItems)) {
                      sections[destinationSectionIndex].items = [];
                 }

                const [movedItem] = sourceSectionItems.splice(source.index, 1);
                sections[destinationSectionIndex].items.splice(destination.index, 0, movedItem);
            }

            saveTableOfContents(updatedTableOfContents);
        }
    };

    const updateSectionData = async (sectionIndex, changeValues) => {
        const updatedTableOfContents = cloneDeep(tableOfContents);
    
        if (!updatedTableOfContents?.contents?.[0]?.sections?.[sectionIndex]) {
            console.error("Section not found at index:", sectionIndex);
            return;
        }
    
        const section = updatedTableOfContents.contents[0].sections[sectionIndex];
    
        for (const [key, value] of Object.entries(changeValues)) {
            section[key] = value;
        }
    
        await saveTableOfContents(updatedTableOfContents);
    };


    const currentSections = tableOfContents?.contents?.[0]?.sections || [];
    
    return (
        <div className={styles.container}>
            <div className={styles.contentWrapper}>
                
                <button onClick={() => setShowTocEditor(false)} className={`${styles.button} ${styles.buttonText} ${styles.backButton}`} title="Back to Editor">
                    <FaArrowLeft />
                    <span>Back</span>
                </button>

                <div className={styles.header}>
                    <h1 className={styles.title}>Table of Contents</h1>
                    </div>

                    <div className={styles.settingsSection}>
                        <h2 className={styles.settingsTitle}>Settings</h2>
                        <div className={styles.checkboxControl}>
                            <input
                                id="toc-disable"
                                type="checkbox"
                                className={styles.checkboxInput}
                                checked={tableOfContents.disabled}
                                onChange={(e) => disableToC(e.target.checked)}
                            />
                            <label htmlFor="toc-disable" className={styles.checkboxLabel}>Disable Table of Contents</label>
                        </div>
                    </div>

                {/* --- Create Section Controls --- */}
                 {!isSectionInputVisible && (
                     <div className={styles.controls}>
                         <button onClick={createSection} className={`${styles.button} ${styles.buttonPrimary}`}>
                             <FaPlus /> Create Section
                         </button>
                     </div>
                )}
                
                {isSectionInputVisible && (
                <div className={styles.sectionInputContainer}>
                    <div className={styles.sectionInput} ref={inputRef}>
                        <input
                            type="text"
                            value={sectionLabel}
                            onChange={(e) => setSectionLabel(e.target.value)}
                            onKeyPress={handleKeyPressSection}
                            placeholder="Enter new section title"
                            className={styles.inputField}
                            aria-label="New section title"
                        />

                        <div className={styles.advancedHeader}>
                            <button
                                className={styles.advancedButton}
                                onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                                aria-expanded={showAdvancedSettings}
                            >
                                {showAdvancedSettings ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
                            </button>
                        </div>

                        <div
                            className={`${styles.advancedSettingsWrapper} ${
                            showAdvancedSettings ? styles.show : ''
                            }`}
                        >
                            <div className={styles.advancedSettings}>
                                <p className={styles.advancedNote}>
                                    Select the behavior for this section.
                                </p>

                                {/* -- Advanced Setting: Behavior -- */}
                                <div className={styles.settingOption}>
                                    <input type="radio" id="always-accessible" name="sectionBehavior" value="alwaysAccessible" checked={sectionBehavior === 'alwaysAccessible'} onChange={(e) => setSectionBehavior(e.target.value)} />
                                    <label htmlFor="always-accessible" className={styles.settingLabel}>
                                        <span className={styles.settingTitle}>Always Accessible (Default)</span>
                                        <p className={styles.optionDescription}>All items are visible and can be navigated to at any time.</p>
                                    </label>
                                </div>
                                <div className={styles.settingOption}>
                                    <input type="radio" id="show-all-visit-only" name="sectionBehavior" value="showAllVisitOnly" checked={sectionBehavior === 'showAllVisitOnly'} onChange={(e) => setSectionBehavior(e.target.value)} />
                                    <label htmlFor="show-all-visit-only" className={styles.settingLabel}>
                                        <span className={styles.settingTitle}>Show All, Visit to Access</span>
                                        <p className={styles.optionDescription}>All items are visible, but can only be navigated to after being visited.</p>
                                    </label>
                                </div>
                                <div className={styles.settingOption}>
                                    <input type="radio" id="reveal-on-visit" name="sectionBehavior" value="revealOnVisit" checked={sectionBehavior === 'revealOnVisit'} onChange={(e) => setSectionBehavior(e.target.value)} />
                                    <label htmlFor="reveal-on-visit" className={styles.settingLabel}>
                                        <span className={styles.settingTitle}>Reveal on Visit</span>
                                        <p className={styles.optionDescription}>Items only appear in the table of contents after they have been visited.</p>
                                    </label>
                                </div>
                                
                                <hr className={styles.separator} />

                                {/* -- Advanced Setting: Add All -- */}
                                <div className={styles.settingOption}>
                                    <input type="checkbox" id="add-all-situations" checked={addAllSituations} onChange={(e) => setAddAllSituations(e.target.checked)} />
                                    <label htmlFor="add-all-situations" className={styles.settingLabel}>
                                        <span className={styles.settingTitle}>Add all scenario situations</span>
                                        <p className={styles.optionDescription}>Automatically populates the section with all situations from the scenario.</p>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className={styles.buttonContainer}>
                            <button
                                onClick={addAllSituations ? handleAddAllSituationsToNewSection : handleAddSection}
                                className={`${styles.button} ${styles.buttonPrimary}`}
                                title="Create Section"
                            >
                                <FaPlus /> Create
                            </button>
                            <button
                                onClick={() => {
                                    setIsSectionInputVisible(false);
                                    setSectionLabel('');
                                    setSectionBehavior('alwaysAccessible');
                                    setAddAllSituations(false);
                                    setShowAdvancedSettings(false);
                                }}
                                className={`${styles.button} ${styles.buttonSecondary}`}
                                title="Cancel"
                            >
                                <FaTimes /> Cancel
                            </button>
                        </div>
                    </div>
                </div>
                )}


                {/* --- Modal --- */}
                 {isModalVisible && (
                    <TableOfContentsModal
                        isVisible={isModalVisible}
                        toggleModal={toggleModal}
                        itemLabel={itemLabel}
                        setItemLabel={setItemLabel}
                        situations={situations}
                        selectedSituation={selectedSituation}
                        setSelectedSituation={setSelectedSituation}
                        handleSave={handleSaveItem}
                        isEditing={currentItemIndex !== null}
                    />
                )}


                {/* --- Drag and Drop Area --- */}
                <DragDropContext onDragEnd={onDragEnd}>
                    {/* Outer Droppable for Sections */}
                    <Droppable droppableId="toc-sections" type="section">
                        {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className={styles.sectionsContainer}>
                                {currentSections.map((section, sectionIndex) => (
                                    <Draggable key={`section-${sectionIndex}`} draggableId={`section-${sectionIndex}`} index={sectionIndex}>
                                        {(providedDraggable, snapshotDraggable) => (
                                            <div
                                                ref={providedDraggable.innerRef}
                                                {...providedDraggable.draggableProps}
                                                className={`${styles.section} ${snapshotDraggable.isDragging ? styles.dragging : ''} ${section.disabled ? styles.disabledSection : ''}`}
                                            >
                                                {/* Section Header */}
                                                <div className={styles.sectionHeader}>
                                                     <span {...providedDraggable.dragHandleProps} className={styles.dragHandleIconContainer} title="Drag to reorder section">
                                                        <FaGripVertical className={styles.dragHandleIcon} />
                                                    </span>
                                                    <div className={styles.sectionTitleContainer}>
                                                        {isEditingSection && currentSectionIndex === sectionIndex ? (
                                                             <div className={styles.sectionEdit} ref={inputRef}>
                                                                <input
                                                                    type="text"
                                                                    value={sectionLabel}
                                                                    onChange={(e) => setSectionLabel(e.target.value)}
                                                                    onKeyPress={(e) => handleSectionEditKeyPress(e, sectionIndex)}
                                                                    placeholder="Section Title"
                                                                    className={styles.inputField}
                                                                    aria-label="Edit section title"
                                                                    autoFocus
                                                                />
                                                                 <button onClick={() => handleSectionEditConfirm(sectionIndex)} className={`${styles.button} ${styles.buttonIcon} ${styles.saveButton}`} title="Save Title">
                                                                    <FaSave />
                                                                </button>
                                                                 <button onClick={cancelEditing} className={`${styles.button} ${styles.buttonIcon} ${styles.cancelButton}`} title="Cancel Edit">
                                                                    <FaTimes />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            
                                                             <span
                                                                className={styles.sectionTitle}
                                                                onClick={() => startEditing(sectionIndex, section.label || "")}
                                                                title="Click to edit title"
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditing(sectionIndex, section.label || ""); }} 
                                                                >
                                                                {section.disabled && <FaEyeSlash className={styles.disabledIcon} title="Section is disabled" />}
                                                                {section.label || "Untitled Section"}
                                                            </span>
                                                            
                                                        )}
                                                    </div>
                                                     {!isEditingSection || currentSectionIndex !== sectionIndex ? (
                                                        <div className={styles.sectionActions}>
                                                            <button
                                                                onClick={(event) => toggleModal(event, sectionIndex)}
                                                                className={`${styles.button} ${styles.buttonText}`}
                                                                title="Add Situation to Section"
                                                            >
                                                                <FaPlus /> Add Situation
                                                            </button>

                                                            <div className={styles.dropdownContainer}>
                                                                <button className={styles.dropdownTrigger} title="Section Settings">
                                                                    <FaBars />
                                                                </button>

                                                                <div className={styles.dropdownMenu}>
                                                                    <div className={styles.dropdownItem}>
                                                                        <input type="checkbox" id={`disable-${sectionIndex}`} className={styles.checkboxInput} checked={section.disabled} onChange={(e) => updateSectionData(sectionIndex, {'disabled': e.target.checked})} />
                                                                        <label htmlFor={`disable-${sectionIndex}`} className={styles.checkboxLabel}>Disable Section</label>
                                                                    </div>
                                                                    <hr className={styles.separator} />
                                                                    <div className={styles.dropdownSectionTitle}>Behavior</div>
                                                                    <div className={styles.dropdownItem}>
                                                                        <input type="radio" id={`behavior-always-${sectionIndex}`} name={`behavior-${sectionIndex}`} checked={section.behavior === 'alwaysAccessible'} onChange={() => updateSectionData(sectionIndex, { 'behavior': 'alwaysAccessible' })} />
                                                                        <label htmlFor={`behavior-always-${sectionIndex}`} className={styles.radioLabel}>Always Accessible</label>
                                                                    </div>
                                                                    <div className={styles.dropdownItem}>
                                                                        <input type="radio" id={`behavior-showall-${sectionIndex}`} name={`behavior-${sectionIndex}`} checked={section.behavior === 'showAllVisitOnly'} onChange={() => updateSectionData(sectionIndex, { 'behavior': 'showAllVisitOnly' })} />
                                                                        <label htmlFor={`behavior-showall-${sectionIndex}`} className={styles.radioLabel}>Show All, Visit to Access</label>
                                                                    </div>
                                                                    <div className={styles.dropdownItem}>
                                                                        <input type="radio" id={`behavior-reveal-${sectionIndex}`} name={`behavior-${sectionIndex}`} checked={section.behavior === 'revealOnVisit'} onChange={() => updateSectionData(sectionIndex, { 'behavior': 'revealOnVisit' })} />
                                                                        <label htmlFor={`behavior-reveal-${sectionIndex}`} className={styles.radioLabel}>Reveal on Visit</label>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <button
                                                                onClick={() => handleDeleteSection(sectionIndex)}
                                                                className={`${styles.button} ${styles.buttonIcon} ${styles.deleteButton}`}
                                                                title="Delete Section"
                                                            >
                                                                <FaTrashAlt />
                                                            </button>
                                                        </div>
                                                    ) : null }
                                                </div>

                                                {/* Section Content */}
                                                <Droppable droppableId={`section-${sectionIndex}`} type="item">
                                                    {(providedDroppable, snapshotDroppable) => (
                                                        <div
                                                            ref={providedDroppable.innerRef}
                                                            {...providedDroppable.droppableProps}
                                                            className={`${styles.sectionContent} ${snapshotDroppable.isDraggingOver ? styles.draggingOver : ''}`}
                                                        >
                                                            {(section.items || []).map((item, itemIndex) => (
                                                                <Draggable key={`item-${sectionIndex}-${itemIndex}`} draggableId={`item-${sectionIndex}-${itemIndex}`} index={itemIndex}>
                                                                    {(providedItem, snapshotItem) => (
                                                                        <div
                                                                            ref={providedItem.innerRef}
                                                                            {...providedItem.draggableProps}
                                                                            className={`${styles.itemContainer} ${snapshotItem.isDragging ? styles.dragging : ''}`}
                                                                        >
                                                                             <span {...providedItem.dragHandleProps} className={styles.itemDragHandle} title="Drag to reorder situation">
                                                                                <FaGripVertical className={styles.dragHandleIcon} />
                                                                            </span>
                                                                            <div
                                                                                className={styles.itemLabel}
                                                                                onClick={(event) => toggleModal(event, sectionIndex, itemIndex, item)}
                                                                                title="Click to edit"
                                                                                role="button"
                                                                                tabIndex={0}
                                                                                onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleModal(e, sectionIndex, itemIndex, item); }}
                                                                            >
                                                                                {item.label || "Untitled Situation"}
                                                                            </div>
                                                                            <button onClick={() => handleDeleteItem(sectionIndex, itemIndex)} className={`${styles.button} ${styles.buttonIcon} ${styles.deleteButton}`} title="Delete Situation">
                                                                                <FaMinus />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {providedDroppable.placeholder}
                                                            {(!section.items || section.items.length === 0) && !snapshotDroppable.isDraggingOver && (
                                                                <div className={styles.emptySectionMessage}>
                                                                    Drag situations here or click 'Add Situation' above.
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </Droppable>
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {currentSections.length === 0 && !isSectionInputVisible && (
                                    <div className={styles.emptyTocMessage}>
                                        No sections created yet. Click 'Create Section' to begin.
                                    </div>
                                )}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};

export default TableOfContents;